import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { Employee, EmployeeFormData, EmployeeFiles } from "@/types/employee";
import { BasicInfoSection } from "./BasicInfoSection";
import { ProfessionalInfoSection } from "./ProfessionalInfoSection";
import { AdditionalInfoSection } from "./AdditionalInfoSection";
import { FileUploadSection } from "./FileUploadSection";
import { RoleAssignmentSection } from "./RoleAssignmentSection";
import { DepartmentAssignmentSection } from "./DepartmentAssignmentSection";
import { UserRoleAssignmentSection } from "./UserRoleAssignmentSection";
import { useEmployeeRoles } from "@/hooks/useRoles";
import { AppRole } from "@/hooks/useUserRoles";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmergencyContactSection, EmergencyContact } from "./EmergencyContactSection";
import { EmployeeLifecycleSection } from "./EmployeeLifecycleSection";

interface EmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingEmployee: Employee | null;
  onSubmit: (formData: EmployeeFormData, files: EmployeeFiles, editingEmployee: Employee | null, roleIds: string[], departmentIds: string[], userRoles?: AppRole[]) => Promise<boolean>;
  uploading: boolean;
}

export const EmployeeDialog = ({ 
  open, 
  onOpenChange, 
  editingEmployee, 
  onSubmit, 
  uploading 
}: EmployeeDialogProps) => {
  const [formData, setFormData] = useState<EmployeeFormData>({
    name: "",
    position: "",
    department: "",
    phone: "",
    email: "",
    status: "present",
    birth_date: "",
    qualifications: "",
    previous_experience: "",
    electronic_accounts: "",
    job_title: "",
    job_responsibilities: "",
    job_description: "",
    employee_number: "",
    hiring_date: "",
    working_hours: 8,
    salary: ""
  });

  const [files, setFiles] = useState<EmployeeFiles>({
    certificate: null,
    national_id: null,
    profile_image: null,
    contract: null
  });

  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [lifecycleData, setLifecycleData] = useState({
    employment_status: "active",
    termination_date: "",
    termination_reason: "",
    onboarding_completed: false,
    offboarding_completed: false,
    onboarding_notes: "",
    offboarding_notes: "",
    last_promotion_date: "",
    next_review_date: "",
    probation_end_date: "",
    contract_expiry_date: ""
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedUserRoles, setSelectedUserRoles] = useState<AppRole[]>([]);
  const { data: employeeRoles, isLoading: isLoadingRoles } = useEmployeeRoles(editingEmployee?.id);

  const resetForm = () => {
    setFormData({
      name: "",
      position: "",
      department: "",
      phone: "",
      email: "",
      status: "present",
      birth_date: "",
      qualifications: "",
      previous_experience: "",
      electronic_accounts: "",
      job_title: "",
      job_responsibilities: "",
      job_description: "",
      employee_number: "",
      hiring_date: "",
      working_hours: 8,
      salary: ""
    });
    setFiles({
      certificate: null,
      national_id: null,
      profile_image: null,
      contract: null
    });
    setSelectedRoles([]);
    setSelectedDepartments([]);
    setSelectedUserRoles([]);
    setEmergencyContacts([]);
    setLifecycleData({
      employment_status: "active",
      termination_date: "",
      termination_reason: "",
      onboarding_completed: false,
      offboarding_completed: false,
      onboarding_notes: "",
      offboarding_notes: "",
      last_promotion_date: "",
      next_review_date: "",
      probation_end_date: "",
      contract_expiry_date: ""
    });
  };

  // تحديث البيانات عند تغيير الموظف المحدد للتعديل
  useEffect(() => {
    if (editingEmployee && open) {
      console.log('Loading employee data for editing:', editingEmployee);
      setFormData({
        name: editingEmployee.name || "",
        position: editingEmployee.position || "",
        department: editingEmployee.department || "",
        phone: editingEmployee.phone || "",
        email: editingEmployee.email || "",
        status: editingEmployee.status || "present",
        birth_date: editingEmployee.birth_date || "",
        qualifications: editingEmployee.qualifications || "",
        previous_experience: editingEmployee.previous_experience || "",
        electronic_accounts: editingEmployee.electronic_accounts ? JSON.stringify(editingEmployee.electronic_accounts) : "",
        job_title: editingEmployee.job_title || "",
        job_responsibilities: editingEmployee.job_responsibilities || "",
        job_description: editingEmployee.job_description || "",
        employee_number: editingEmployee.employee_number || "",
        hiring_date: editingEmployee.hiring_date || "",
        working_hours: editingEmployee.working_hours || 8,
        salary: editingEmployee.salary ? editingEmployee.salary.toString() : ""
      });
      setSelectedDepartments(editingEmployee.departments?.map(d => d.id) || []);
      
      // تحديث بيانات دورة الحياة
      setLifecycleData({
        employment_status: editingEmployee.employment_status || "active",
        termination_date: editingEmployee.termination_date || "",
        termination_reason: editingEmployee.termination_reason || "",
        onboarding_completed: editingEmployee.onboarding_completed || false,
        offboarding_completed: editingEmployee.offboarding_completed || false,
        onboarding_notes: editingEmployee.onboarding_notes || "",
        offboarding_notes: editingEmployee.offboarding_notes || "",
        last_promotion_date: editingEmployee.last_promotion_date || "",
        next_review_date: editingEmployee.next_review_date || "",
        probation_end_date: editingEmployee.probation_end_date || "",
        contract_expiry_date: editingEmployee.contract_expiry_date || ""
      });
    } else if (!open) {
      resetForm();
    }
  }, [editingEmployee, open]);
  
  useEffect(() => {
    if (employeeRoles && editingEmployee) {
        setSelectedRoles(employeeRoles.map(er => er.role_id));
    } else if (!editingEmployee) {
        setSelectedRoles([]);
    }
  }, [employeeRoles, editingEmployee]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من وجود البيانات المطلوبة فقط
    if (!formData.name.trim()) {
      alert("يرجى إدخال اسم الموظف");
      return;
    }
    
    if (!formData.email.trim()) {
      alert("يرجى إدخال البريد الإلكتروني");
      return;
    }
    
    const success = await onSubmit(formData, files, editingEmployee, selectedRoles, selectedDepartments, selectedUserRoles);
    if (success) {
      onOpenChange(false);
      if (!editingEmployee) {
        resetForm();
      }
    }
  };

  const handleFileChange = (type: keyof EmployeeFiles, file: File | null) => {
    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleDepartmentToggle = (departmentId: string) => {
    setSelectedDepartments(prev => 
      prev.includes(departmentId) 
        ? prev.filter(id => id !== departmentId)
        : [...prev, departmentId]
    );
  };
  
  const handleUserRoleToggle = (role: AppRole) => {
    setSelectedUserRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>{editingEmployee ? "تعديل الموظف" : "إضافة موظف جديد"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">أساسي</TabsTrigger>
              <TabsTrigger value="emergency">طوارئ</TabsTrigger>
              <TabsTrigger value="lifecycle">دورة الحياة</TabsTrigger>
              <TabsTrigger value="roles">الأدوار</TabsTrigger>
              <TabsTrigger value="files">الملفات</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <BasicInfoSection formData={formData} setFormData={setFormData} />
              <ProfessionalInfoSection formData={formData} setFormData={setFormData} />
              <DepartmentAssignmentSection selectedDepartments={selectedDepartments} onDepartmentToggle={handleDepartmentToggle} />
              <AdditionalInfoSection formData={formData} setFormData={setFormData} />
            </TabsContent>

            <TabsContent value="emergency" className="mt-4">
              <EmergencyContactSection 
                contacts={emergencyContacts}
                onChange={setEmergencyContacts}
              />
            </TabsContent>

            <TabsContent value="lifecycle" className="mt-4">
              <EmployeeLifecycleSection
                data={lifecycleData}
                onChange={(field, value) => setLifecycleData(prev => ({ ...prev, [field]: value }))}
              />
            </TabsContent>

            <TabsContent value="roles" className="mt-4">
              <Tabs defaultValue="custom" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="custom">الأدوار المخصصة</TabsTrigger>
                  <TabsTrigger value="system">أدوار النظام (RBAC)</TabsTrigger>
                </TabsList>
                <TabsContent value="custom" className="space-y-4">
                  <RoleAssignmentSection selectedRoles={selectedRoles} onRoleToggle={handleRoleToggle} />
                </TabsContent>
                <TabsContent value="system" className="space-y-4">
                  <UserRoleAssignmentSection 
                    userId={editingEmployee?.user_id || null} 
                    selectedRoles={selectedUserRoles} 
                    onRoleToggle={handleUserRoleToggle} 
                  />
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <FileUploadSection files={files} onFileChange={handleFileChange} />
            </TabsContent>
          </Tabs>

          <Button type="submit" className="w-full" disabled={uploading || isLoadingRoles}>
            {isLoadingRoles ? "جاري تحميل الأدوار..." : uploading ? "جاري الحفظ..." : (editingEmployee ? "تحديث" : "إضافة")}
            {(uploading || isLoadingRoles) && <Upload className="w-4 h-4 mr-2 animate-spin" />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
